import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Columns3 } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { IconButton } from './button';
import { EmptyState, Skeleton, type EmptyStateProps } from './display';
import {
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuLabel,
  MenuTrigger,
} from './overlay';
import { CheckboxControl } from './toggle';

/* ============================================================================
   Data table.

   Deliberately hand-built rather than pulled from a table library: the product
   has one data-dense surface shape (a list of a few hundred owned resources with
   sort, filter, selection and row actions), and this covers it in a fraction of
   the API surface a headless table engine brings. Revisit if pivoting, grouping
   or virtualised thousands of rows ever become real requirements.

   Responsive by design: below `md` the same rows render through `renderCard`,
   because a five-column table on a phone is not a table, it is a horizontal
   scroll nobody uses.
   ========================================================================== */

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** Cell renderer. Keep it presentational — sorting reads `sortValue`. */
  cell: (row: T) => ReactNode;
  /** Comparable value for sorting. Omit to make the column unsortable. */
  sortValue?: (row: T) => string | number;
  width?: string;
  align?: 'left' | 'right';
  /** Hide on narrow viewports (the card view still shows the data). */
  hideBelow?: 'lg' | 'xl';
  /** Let the user hide this column from the column menu. */
  hideable?: boolean;
  className?: string;
}

export interface SortState {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  rows: T[];
  columns: ReadonlyArray<Column<T>>;
  rowKey: (row: T) => string;
  loading?: boolean;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Selected row keys. Providing this enables the selection column. */
  selected?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;
  onRowClick?: (row: T) => void;
  /** Marks the row as the active/selected one (for a list-detail layout). */
  isActive?: (row: T) => boolean;
  /** Right-aligned per-row controls; not part of the sortable columns. */
  rowActions?: (row: T) => ReactNode;
  /** Card body for the sub-`md` layout. */
  renderCard?: (row: T) => ReactNode;
  hiddenColumns?: ReadonlySet<string>;
  onHiddenColumnsChange?: (next: Set<string>) => void;
  empty: EmptyStateProps;
  className?: string;
  /** Accessible name for the grid. */
  label: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading,
  sort,
  onSortChange,
  selected,
  onSelectedChange,
  onRowClick,
  isActive,
  rowActions,
  renderCard,
  hiddenColumns,
  empty,
  className,
  label,
}: DataTableProps<T>) {
  const visible = useMemo(
    () => columns.filter((c) => !hiddenColumns?.has(c.id)),
    [columns, hiddenColumns],
  );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [rows, columns, sort]);

  const selectable = Boolean(selected && onSelectedChange);
  const allSelected = selectable && sorted.length > 0 && sorted.every((r) => selected!.has(rowKey(r)));
  const someSelected = selectable && !allSelected && sorted.some((r) => selected!.has(rowKey(r)));

  const toggleAll = () => {
    if (!onSelectedChange) return;
    onSelectedChange(allSelected ? new Set() : new Set(sorted.map(rowKey)));
  };

  const toggleOne = (key: string) => {
    if (!onSelectedChange || !selected) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectedChange(next);
  };

  const headerButton = (col: Column<T>) => {
    const active = sort?.columnId === col.id;
    if (!col.sortValue || !onSortChange) return col.header;
    return (
      <button
        type="button"
        onClick={() =>
          onSortChange({
            columnId: col.id,
            direction: active && sort?.direction === 'asc' ? 'desc' : 'asc',
          })
        }
        className="group inline-flex items-center gap-1 rounded-xs text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        {col.header}
        <span className={cn('transition-opacity', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40')}>
          {active && sort?.direction === 'desc' ? (
            <ArrowDown className="size-3" />
          ) : (
            <ArrowUp className="size-3" />
          )}
        </span>
      </button>
    );
  };

  if (!loading && sorted.length === 0) {
    return (
      <div className={className}>
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Card layout — narrow viewports */}
      {renderCard && (
        <ul className="flex flex-col gap-2 md:hidden" aria-label={label}>
          {loading
            ? Array.from({ length: 4 }, (_, i) => (
                <li key={i}>
                  <Skeleton className="h-16" />
                </li>
              ))
            : sorted.map((row) => {
                const key = rowKey(row);
                return (
                  <li key={key} className="relative">
                    <div
                      className={cn(
                        'rounded-lg border border-border bg-surface p-3',
                        isActive?.(row) && 'border-accent-border bg-accent-subtle',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {selectable && (
                          <CheckboxControl
                            checked={selected!.has(key)}
                            onCheckedChange={() => toggleOne(key)}
                            aria-label="Select row"
                            className="mt-1"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => onRowClick?.(row)}
                          className="min-w-0 flex-1 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                        >
                          {renderCard(row)}
                        </button>
                        {rowActions?.(row)}
                      </div>
                    </div>
                  </li>
                );
              })}
        </ul>
      )}

      {/* Table layout */}
      <div className={cn('overflow-x-auto', renderCard && 'hidden md:block')}>
        <table className="w-full border-collapse text-[0.8125rem]" aria-label={label}>
          <thead>
            <tr className="border-b border-border">
              {selectable && (
                <th scope="col" className="w-9 px-3 py-2">
                  <CheckboxControl
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label={allSelected ? 'Clear selection' : 'Select all rows'}
                  />
                </th>
              )}
              {visible.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={
                    sort?.columnId === col.id
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : col.sortValue
                        ? 'none'
                        : undefined
                  }
                  className={cn(
                    'whitespace-nowrap px-3 py-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-fg-faint',
                    col.align === 'right' ? 'text-right' : 'text-left',
                    col.hideBelow === 'lg' && 'hidden lg:table-cell',
                    col.hideBelow === 'xl' && 'hidden xl:table-cell',
                  )}
                >
                  {headerButton(col)}
                </th>
              ))}
              {rowActions && <th scope="col" className="w-16 px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }, (_, i) => (
                  <tr key={i} className="border-b border-border-subtle">
                    {selectable && <td className="px-3 py-2.5" />}
                    {visible.map((col) => (
                      <td key={col.id} className="px-3 py-2.5">
                        <Skeleton className="h-3.5" />
                      </td>
                    ))}
                    {rowActions && <td />}
                  </tr>
                ))
              : sorted.map((row) => {
                  const key = rowKey(row);
                  const active = isActive?.(row);
                  return (
                    <tr
                      key={key}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn(
                        'group border-b border-border-subtle transition-colors',
                        onRowClick && 'cursor-pointer',
                        active ? 'bg-accent-subtle' : 'hover:bg-surface-hover',
                      )}
                    >
                      {selectable && (
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <CheckboxControl
                            checked={selected!.has(key)}
                            onCheckedChange={() => toggleOne(key)}
                            aria-label="Select row"
                          />
                        </td>
                      )}
                      {visible.map((col) => (
                        <td
                          key={col.id}
                          className={cn(
                            'px-3 py-2 text-fg-secondary',
                            col.align === 'right' && 'text-right',
                            col.hideBelow === 'lg' && 'hidden lg:table-cell',
                            col.hideBelow === 'xl' && 'hidden xl:table-cell',
                            col.className,
                          )}
                        >
                          {col.cell(row)}
                        </td>
                      ))}
                      {rowActions && (
                        <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          {rowActions(row)}
                        </td>
                      )}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The column-visibility menu that belongs with a `DataTable`. */
export function ColumnMenu<T>({
  columns,
  hidden,
  onHiddenChange,
}: {
  columns: ReadonlyArray<Column<T>>;
  hidden: ReadonlySet<string>;
  onHiddenChange: (next: Set<string>) => void;
}) {
  const hideable = columns.filter((c) => c.hideable);
  if (!hideable.length) return null;
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton label="Choose columns" variant="secondary">
          <Columns3 />
        </IconButton>
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>Columns</MenuLabel>
        {hideable.map((c) => (
          <MenuCheckboxItem
            key={c.id}
            checked={!hidden.has(c.id)}
            onCheckedChange={(on) => {
              const next = new Set(hidden);
              if (on) next.delete(c.id);
              else next.add(c.id);
              onHiddenChange(next);
            }}
            onSelect={(e) => e.preventDefault()}
          >
            {c.header}
          </MenuCheckboxItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

export interface PaginationProps {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function Pagination({ page, pageCount, total, pageSize, onPageChange, className }: PaginationProps) {
  if (pageCount <= 1) return null;
  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);
  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-fg-muted', className)}
    >
      <span>
        <span className="tabular">
          {first}–{last}
        </span>{' '}
        of <span className="tabular">{total}</span>
      </span>
      <div className="flex items-center gap-1">
        <IconButton
          label="Previous page"
          size="sm"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft />
        </IconButton>
        <span className="px-1 tabular">
          {page + 1} / {pageCount}
        </span>
        <IconButton
          label="Next page"
          size="sm"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight />
        </IconButton>
      </div>
    </nav>
  );
}
